import fs from 'fs-extra';
import { join } from 'path';
import chalk from 'chalk';
import ora from 'ora';
import type {
  CacheProvider,
  CacheItem,
  CacheMeta,
  CacheStats,
  CacheConfig,
} from './types';

export class LocalCache implements CacheProvider {
  private cacheDir: string;
  private spinner = ora();
  private config: CacheConfig['local'];
  private stats: CacheStats = {
    totalItems: 0,
    totalSize: 0,
    hits: 0,
    misses: 0,
    hitRate: 0,
    avgAccessTime: 0,
    topItems: [],
  };

  constructor(
    cacheDir: string = '.synthia-cache',
    config?: Partial<CacheConfig['local']>
  ) {
    this.cacheDir = cacheDir;
    this.config = {
      dir: cacheDir,
      maxSize: 100 * 1024 * 1024, // 100MB
      ttl: 7 * 24 * 60 * 60 * 1000, // 7天
      compression: false,
      ...config,
    };
    this.ensureCacheDir();
    this.loadStats();
  }

  /**
   * 设置缓存
   */
  async set(
    key: string,
    value: any,
    meta: Partial<CacheMeta> = {}
  ): Promise<void> {
    this.spinner.start(`设置本地缓存: ${key}`);

    try {
      const startTime = Date.now();
      const cacheItem: CacheItem = {
        key,
        value,
        meta: {
          hash: meta.hash || this.calculateObjectHash(value),
          size: meta.size || JSON.stringify(value).length,
          expiresAt: meta.expiresAt || Date.now() + this.config.ttl,
          dependencies: meta.dependencies || [],
          tags: meta.tags || [],
          version: meta.version || '1.0.0',
        },
        createdAt: Date.now(),
        lastAccessedAt: Date.now(),
        accessCount: 0,
      };

      const cachePath = this.getCachePath(key);
      fs.writeFileSync(cachePath, JSON.stringify(cacheItem, null, 2));

      // 更新统计信息
      this.updateStats('set', key, Date.now() - startTime, true);

      this.spinner.succeed(`本地缓存设置完成: ${key}`);
    } catch (error) {
      this.spinner.fail(`本地缓存设置失败: ${key}`);
      console.error(chalk.red('缓存错误:'), error);
      this.updateStats(
        'set',
        key,
        0,
        false,
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  }

  /**
   * 获取缓存
   */
  async get(key: string): Promise<CacheItem | null> {
    this.spinner.start(`获取本地缓存: ${key}`);

    try {
      const startTime = Date.now();
      const cachePath = this.getCachePath(key);

      if (!fs.existsSync(cachePath)) {
        this.spinner.warn(`缓存不存在: ${key}`);
        this.updateStats('get', key, Date.now() - startTime, false);
        return null;
      }

      const cacheItem: CacheItem = JSON.parse(
        fs.readFileSync(cachePath, 'utf-8')
      );

      // 检查是否过期
      if (cacheItem.meta.expiresAt < Date.now()) {
        this.spinner.warn(`缓存已过期: ${key}`);
        await this.delete(key);
        this.updateStats('get', key, Date.now() - startTime, false);
        return null;
      }

      // 更新访问信息
      cacheItem.lastAccessedAt = Date.now();
      cacheItem.accessCount++;
      fs.writeFileSync(cachePath, JSON.stringify(cacheItem, null, 2));

      this.spinner.succeed(`本地缓存获取成功: ${key}`);
      this.updateStats('get', key, Date.now() - startTime, true);
      return cacheItem;
    } catch (error) {
      this.spinner.fail(`本地缓存获取失败: ${key}`);
      console.error(chalk.red('缓存错误:'), error);
      this.updateStats(
        'get',
        key,
        0,
        false,
        error instanceof Error ? error.message : 'Unknown error'
      );
      return null;
    }
  }

  /**
   * 删除缓存
   */
  async delete(key: string): Promise<void> {
    try {
      const startTime = Date.now();
      const cachePath = this.getCachePath(key);

      if (fs.existsSync(cachePath)) {
        require('fs').unlinkSync(cachePath);
        this.updateStats('delete', key, Date.now() - startTime, true);
        console.log(chalk.green(`缓存已删除: ${key}`));
      } else {
        this.updateStats('delete', key, Date.now() - startTime, false);
      }
    } catch (error) {
      console.error(chalk.red('删除缓存失败:'), error);
      this.updateStats(
        'delete',
        key,
        0,
        false,
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  }

  /**
   * 清空所有缓存
   */
  async clear(): Promise<void> {
    this.spinner.start('清空本地缓存...');

    try {
      const startTime = Date.now();
      if (fs.existsSync(this.cacheDir)) {
        const files = fs.readdirSync(this.cacheDir);
        for (const file of files) {
          const filePath = join(this.cacheDir, file);
          const stats = fs.statSync(filePath);

          if (stats.isFile()) {
            require('fs').unlinkSync(filePath);
          } else if (stats.isDirectory()) {
            require('fs').rmSync(filePath, { recursive: true, force: true });
          }
        }
      }

      // 重置统计信息
      this.stats = {
        totalItems: 0,
        totalSize: 0,
        hits: 0,
        misses: 0,
        hitRate: 0,
        avgAccessTime: 0,
        topItems: [],
      };
      this.saveStats();

      this.spinner.succeed('本地缓存已清空');
      this.updateStats('clear', '', Date.now() - startTime, true);
    } catch (error) {
      this.spinner.fail('清空本地缓存失败');
      console.error(chalk.red('清空错误:'), error);
      this.updateStats(
        'clear',
        '',
        0,
        false,
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  }

  /**
   * 检查缓存是否存在
   */
  async has(key: string): Promise<boolean> {
    const cachePath = this.getCachePath(key);
    return fs.existsSync(cachePath);
  }

  /**
   * 获取缓存统计信息
   */
  async getStats(): Promise<CacheStats> {
    await this.refreshStats();
    return { ...this.stats };
  }

  /**
   * 获取所有缓存键
   */
  async keys(): Promise<string[]> {
    if (!fs.existsSync(this.cacheDir)) {
      return [];
    }

    const files = fs.readdirSync(this.cacheDir);
    return files
      .filter(file => file.endsWith('.cache'))
      .map(file => file.replace('.cache', ''));
  }

  /**
   * 获取缓存大小
   */
  async size(): Promise<number> {
    await this.refreshStats();
    return this.stats.totalSize;
  }

  /**
   * 获取缓存路径
   */
  private getCachePath(key: string): string {
    return join(this.cacheDir, `${key}.cache`);
  }

  /**
   * 确保缓存目录存在
   */
  private ensureCacheDir(): void {
    if (!fs.existsSync(this.cacheDir)) {
      fs.mkdirSync(this.cacheDir, { recursive: true });
    }
  }

  /**
   * 更新统计信息
   */
  private updateStats(
    type: 'get' | 'set' | 'delete' | 'clear',
    _key: string,
    duration: number,
    success: boolean,
    _error?: string
  ): void {
    if (type === 'get') {
      if (success) {
        this.stats.hits++;
      } else {
        this.stats.misses++;
      }
    }

    // 更新命中率
    const total = this.stats.hits + this.stats.misses;
    this.stats.hitRate = total > 0 ? this.stats.hits / total : 0;

    // 更新平均访问时间
    if (type === 'get' && success) {
      const totalTime =
        this.stats.avgAccessTime * (this.stats.hits - 1) + duration;
      this.stats.avgAccessTime = totalTime / this.stats.hits;
    }

    this.saveStats();
  }

  /**
   * 刷新统计信息
   */
  private async refreshStats(): Promise<void> {
    if (!fs.existsSync(this.cacheDir)) {
      return;
    }

    const files = fs.readdirSync(this.cacheDir);
    const cacheFiles = files.filter(file => file.endsWith('.cache'));

    let totalSize = 0;
    const topItems: Array<{ key: string; accessCount: number; size: number }> =
      [];

    for (const file of cacheFiles) {
      try {
        const cachePath = join(this.cacheDir, file);
        const cacheItem: CacheItem = JSON.parse(
          fs.readFileSync(cachePath, 'utf-8')
        );

        totalSize += cacheItem.meta.size;
        topItems.push({
          key: cacheItem.key,
          accessCount: cacheItem.accessCount,
          size: cacheItem.meta.size,
        });
      } catch (error) {
        // 忽略损坏的缓存文件
        continue;
      }
    }

    this.stats.totalItems = cacheFiles.length;
    this.stats.totalSize = totalSize;
    this.stats.topItems = topItems
      .sort((a, b) => b.accessCount - a.accessCount)
      .slice(0, 10);
  }

  /**
   * 加载统计信息
   */
  private loadStats(): void {
    const statsPath = join(this.cacheDir, '.stats.json');
    if (fs.existsSync(statsPath)) {
      try {
        const statsData = JSON.parse(fs.readFileSync(statsPath, 'utf-8'));
        this.stats = { ...this.stats, ...statsData };
      } catch (error) {
        // 忽略损坏的统计文件
      }
    }
  }

  /**
   * 保存统计信息
   */
  private saveStats(): void {
    const statsPath = join(this.cacheDir, '.stats.json');
    fs.writeFileSync(statsPath, JSON.stringify(this.stats, null, 2));
  }

  /**
   * 计算对象哈希
   */
  private calculateObjectHash(obj: any): string {
    const hash = require('crypto').createHash('sha256');
    hash.update(JSON.stringify(obj));
    return hash.digest('hex');
  }
}
